//https://confluence.consumer.garmin.com/pages/viewpage.action?spaceKey=DI&title=Elevation+Gain+Analyzer
import _ from 'underscore';

import Personalizer from 'personalizer';

/**
 * Analyzer that calculates the elevation gain for an activity.
 */
var ElevationGainAnalyzer = function ElevationGainAnalyzer(elevationGainThreshold) {
    this.thresholdStartingPoint = null;
    this.valueAgainstThreshold = null;
    this.elevationGain = 0;
    this.elevationGainThreshold = elevationGainThreshold; // The threshold in meters used to filter out variations in altitude. Varies based on source of the data
    this.elevationGainBucket = [];

    if (typeof this.elevationGainThreshold === 'undefined' || this.elevationGainThreshold === null) {
        throw 'Elevation gain threshold required for ElevationGainAnalyzer';
    }
};

function updateBucket(bucket, point, value) {
    bucket.push({
        point: point,
        value: value
    });
}

/**
 * Used to notify the analyzer of a new point.  Should recalculate the elevation gain of the new point.
 * @param point: The point to add.
 */
ElevationGainAnalyzer.prototype.notifyNewPoint = function notifyNewPoint(point) {
    // No elevation gain. This will be for older software of the Fit app. Calculate it ourselves.
    var currentElevation = point.elevation;

    if (currentElevation === null) {
        return;
    }

    if (this.thresholdStartingPoint === null) {
        this.thresholdStartingPoint = point;
        updateBucket(this.elevationGainBucket, point, 0);
        return;
    }

    var thresholdStartPointElevation = this.thresholdStartingPoint.elevation;
    var elevationDifference = currentElevation - thresholdStartPointElevation;

    this.valueAgainstThreshold += elevationDifference;

    if (Math.abs(this.valueAgainstThreshold) >= this.elevationGainThreshold) {
        if (this.valueAgainstThreshold > 0) {
            this.elevationGain += elevationDifference;
            updateBucket(this.elevationGainBucket, point, elevationDifference);
        } else {
            updateBucket(this.elevationGainBucket, point, 0);
        }

        this.valueAgainstThreshold = 0;
        this.thresholdStartingPoint = point;
    } else {
        updateBucket(this.elevationGainBucket, point, 0);
    }
};

ElevationGainAnalyzer.prototype.getElevationGainForRange = function getElevationGainForRange(startPoint, endPoint) {
    var elevation = this.getValue(),
        beforeStartPoint = 0,
        beforeStartPointCount = 0,
        afterEndPoint = 0;

    function isMatch(pointToCompare, pointComparedTo) {
        return (
            Personalizer.convertElevationToMeter(pointToCompare.elevation) === pointComparedTo.elevation &&
            pointToCompare.lat === pointComparedTo.lat &&
            pointToCompare.lng === pointComparedTo.lng &&
            Personalizer.revertDistance(pointToCompare.distance) === pointComparedTo.distance
        );
    }

    if (elevation > 0) {
        for (var e = 0, len = this.elevationGainBucket.length; e < len; e += 1) {
            if (this.elevationGainBucket[e]) {
                if (!isMatch(this.elevationGainBucket[e].point, startPoint)) {
                    beforeStartPoint += this.elevationGainBucket[e].value;
                    beforeStartPointCount++;
                } else {
                    beforeStartPoint += this.elevationGainBucket[e].value;
                    break;
                }
            }
        }

        if (!endPoint) {
            if (beforeStartPoint >= 0) {
                if (beforeStartPointCount === this.elevationGainBucket.length) {
                    return elevation;
                }

                if (beforeStartPoint !== elevation) {
                    return Math.abs(elevation - beforeStartPoint);
                }
            }

            return 0;
        } else {
            for (var i = this.elevationGainBucket.length + 1; i >= 0; i -= 1) {
                if (this.elevationGainBucket[i]) {
                    if (!isMatch(this.elevationGainBucket[i].point, endPoint)) {
                        afterEndPoint += this.elevationGainBucket[i].value;
                    } else {
                        break;
                    }
                }
            }

            var totalElevationToMinus = beforeStartPoint + afterEndPoint;
            if (_.isNaN(totalElevationToMinus) || !_.isNumber(totalElevationToMinus)) {
                return 0;
            }

            if (totalElevationToMinus !== elevation) {
                return Math.abs(elevation - totalElevationToMinus);
            }

            return 0;
        }
    }

    return 0;
};

/**
 * Returns the current elevation gain.
 */
ElevationGainAnalyzer.prototype.getValue = function getValue() {
    return this.elevationGain;
};

ElevationGainAnalyzer.prototype.reset = function reset() {
    this.elevationGain = 0;
    this.thresholdStartingPoint = null;
    this.valueAgainstThreshold = null;
    this.elevationGainBucket = [];
};

export default ElevationGainAnalyzer;
